import { GAEventName } from 'analytics/ga-event-name.enum';
import { ShortLocaleEnum } from 'schema/locale';
import { StoreCode } from 'schema/storeCode';
import { NAVIGATION_TYPE } from '../constants';
import { createAnalyticsEvent } from '../create-analytics-event';

export const buttonClickEvent = createAnalyticsEvent<{
  clickText?: string;
  url?: string;
  location: string;
}>(({ clickText, url, location }) => ({
  event: GAEventName.BUTTON_CLICK,
  click_text: clickText,
  click_url: url,
  location
}));

export const navigationClickEvent = createAnalyticsEvent<{
  header?: string;
  subheader?: string;
  location: string;
  clickText?: string;
  navigationType: keyof typeof NAVIGATION_TYPE;
}>(({ header, subheader, location, clickText, navigationType }) => ({
  event: GAEventName.NAVIGATION_CLICK,
  main_header: header,
  sub_header: subheader,
  location,
  click_text: clickText,
  navigation_type: navigationType
}));

export const menuNavigationEvent = createAnalyticsEvent<{
  header?: string;
  location: string;
  clickText?: string;
}>(({ header, location, clickText }) => ({
  event: GAEventName.MENU_NAV,
  main_header: header,
  location,
  click_text: clickText
}));

export const internalLinkClickEvent = createAnalyticsEvent<{
  clickText?: string;
  url?: string;
  location: string;
}>(({ clickText, url, location }) => ({
  event: GAEventName.INTERNAL_LINK_CLICK,
  click_text: clickText,
  click_url: url,
  location
}));

export const videoStartEvent = createAnalyticsEvent<{
  videoTitle?: string;
  videoProvider?: 'youtube';
  videoUrl?: string;
  videoDuration?: number;
}>(({ videoTitle, videoProvider, videoUrl, videoDuration }) => ({
  event: GAEventName.VIDEO_START,
  video_title: videoTitle,
  video_provider: videoProvider,
  video_url: videoUrl,
  video_duration: videoDuration
}));

export const languageChangeEvent = createAnalyticsEvent<ShortLocaleEnum>((language) => ({
  event: GAEventName.LANGUAGE_PREFERENCE,
  content_language: language
}));

export const currencyChangeEvent = createAnalyticsEvent<StoreCode>((store) => ({
  event: GAEventName.CURRENCY_PREFERENCE,
  default_currency: store
}));

export const newsletterSignupEvent = createAnalyticsEvent<{
  store: StoreCode;
  success: boolean;
  title?: string;
  location: string;
  errorMessage?: string;
  email?: string;
}>(({ success, title, location, errorMessage, email, store }) => ({
  store,
  event: success ? GAEventName.NEWSLETTER_SIGNUP_SUCCESS : GAEventName.NEWSLETTER_SIGNUP_FAILURE,
  newsletter_title: title,
  location,
  email,
  failure_reason: errorMessage || undefined
}));

export const logoutEvent = createAnalyticsEvent(() => ({
  event: GAEventName.LOGOUT
}));

export const clickPurchaseIntentEvent = createAnalyticsEvent<{
  store: StoreCode;
  clickText?: string;
  url?: string;
  location: string;
}>(({ clickText, url, location, store }) => ({
  store,
  event: GAEventName.PURCHASE_INTENT_CTA,
  click_text: clickText,
  click_url: url,
  location
}));

export const selectProductEvent = createAnalyticsEvent<{
  store: StoreCode;
  clickText?: string;
  itemId?: number | string;
  itemName?: string;
  location?: string;
  flavor?: string;
  isStandaloneProduct?: boolean;
}>(({ clickText, itemId, itemName, location, store, flavor, isStandaloneProduct }) => ({
  event: GAEventName.PRODUCT_SELECTION,
  click_text: clickText,
  item_id: itemId,
  item_name: itemName,
  location,
  store,
  flavor,
  isStandaloneProduct
}));

export const SubscriptionSelectionBaBEvent = createAnalyticsEvent<{
  store: StoreCode;
  clickText?: string;
  itemId?: number | string;
  itemName?: string;
  subscriptionType?: string;
  location?: string;
}>(({ clickText, itemId, itemName, subscriptionType, store, location }) => ({
  event: GAEventName.SUBSCRIPTION_SELECTION,
  click_text: clickText,
  item_id: itemId,
  item_name: itemName,
  subscription_type: subscriptionType,
  store,
  location
}));

export const subAndSaveToggleClickEvent = createAnalyticsEvent<{
  store: StoreCode;
  itemId?: number | string;
  itemName?: string;
  location?: string;
  subscriptionType?: string;
}>(({ itemId, itemName, location, store, subscriptionType }) => ({
  event: GAEventName.SUB_AND_SAVE_TOGGLE,
  item_id: itemId,
  item_name: itemName,
  subscription_type: subscriptionType,
  location,
  store
}));

export const simplifiedBaBToggleClickEvent = createAnalyticsEvent<{
  store: StoreCode;
  itemId?: number | string;
  itemName?: string;
  location?: string;
}>(({ itemId, itemName, location, store }) => ({
  event: GAEventName.SIMPLIFIED_BAB_TOGGLE,
  item_id: itemId,
  item_name: itemName,
  location,
  store
}));

export const FrequencySelectionBaBEvent = createAnalyticsEvent<{
  clickText?: string;
  frequency?: string;
  product?: string;
}>(({ clickText, frequency, product }) => ({
  event: GAEventName.FREQUENCY_SELECTION,
  click_text: clickText,
  frequency,
  product
}));

export const viewContentEvent = createAnalyticsEvent<{
  contents: ({
    variant_id?: string;
    id: number | string;
    title: string;
    category: string;
    price: number;
    quantity: number;
    image_url?: string;
  } | null)[];
  eventId: string;
  currency?: string;
  value?: number;
  deviceType?: string;
}>(({ contents, eventId, currency, value, deviceType }) => ({
  event: GAEventName.VIEW_CONTENT,
  contents,
  event_id: eventId,
  currency,
  value,
  deviceType
}));

export const popupViewedEvent = createAnalyticsEvent<{
  clickText?: string;
  popupTitle?: string | null;
  url?: string;
  location: string;
}>(({ clickText, popupTitle, url, location }) => ({
  event: GAEventName.POPUP_VIEWED,
  click_text: clickText,
  popup_title: popupTitle,
  tab_url: url,
  location
}));

export const emailSubmissionEvent = createAnalyticsEvent<{
  clickText?: string;
  popupTitle?: string | null;
  url?: string;
  location: string;
  email: string;
}>(({ clickText, popupTitle, url, location, email }) => ({
  event: GAEventName.EMAIL_SUBMISSION,
  click_url: url,
  click_text: clickText,
  email,
  location,
  popupTitle
}));

export const phoneSubmissionEvent = createAnalyticsEvent<{
  clickText?: string;
  popupTitle?: string | null;
  url?: string;
  location: string;
  phone: string;
}>(({ clickText, popupTitle, url, location, phone }) => ({
  event: GAEventName.PHONE_SUBMISSION,
  click_url: url,
  click_text: clickText,
  phone,
  location,
  popupTitle
}));
